/**
 * WarRoomPage.tsx - War Room V9 Command Center
 * 
 * Dark theme command center with section hierarchy from WarRoomV9_Mockup_v3.jsx.
 * 
 * SECTIONS:
 * 0. Game Objective Banner - Expandable "How It Works" guidance (coaching metaphor)
 * 1. Command Header - Dark theme, coach identity, Strategy Effectiveness %, status pills
 * 2. Mission Critical - Hero card for incomplete < 60 min (amber gradient, progress bars)
 * 3. Needs Attention - Other incomplete pools (amber, fill gaps CTA)
 * 4. Optimize - Slots full, cap not fully deployed (indigo, update roster CTA)
 * 5. On The Field - Live campaigns (green accent, single Scoreboard CTA)
 * 6. In The Locker Room - Ready campaigns (reassurance text)
 * 7. Final — Review - Completed campaigns
 * 
 * Navigation:
 * - COMPLETE ROSTER → /pools/:poolId/tunnel
 * - FILL GAPS → /pools/:poolId/tunnel
 * - SCOREBOARD → /pools/:poolId/leaderboard
 * - EDIT ROSTER → /pools/:poolId/tunnel
 * - VIEW RESULTS → /pools/:poolId/leaderboard
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePools } from '../hooks/usePools';
import { useMemberships } from '../hooks/useMemberships';
import { useWarRoomOverview } from '../hooks/useWarRoomOverview';
import {
  mapToWarRoomCards,
  getHeroCandidate,
  getCriticalListCampaigns,
  getLiveCampaigns,
  getCompleteCampaigns,
  getFinalCampaigns,
  getSlotsFullCapAvailable,
  sortByLockTime,
  WarRoomCampaignCard,
  PoolMembership,
} from '../utils/warRoomMappers';

// =============================================================================
// Icons (Lucide-style SVG components)
// =============================================================================

const FlameIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
  </svg>
);

const PlayIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const ShieldIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const AlertTriangleIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LockIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const ChevronRightIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const TargetIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);



const TrophyIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);


const RefreshIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// =============================================================================
// GAME OBJECTIVE BANNER - Expandable "How It Works" Guidance (V9 Mockup)
// =============================================================================

const ObjectiveBanner = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-6">
      {/* Compact Teaser */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full bg-indigo-900/20 rounded-xl p-4 border border-indigo-500/20 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">🏆</span>
          <div className="flex-1">
            <p className="text-[13px] text-slate-300 leading-snug">
              <span className="text-white font-bold">Coach your team better than everyone else.</span>{' '}
              Build your 7-player roster, set performance targets, and outscore rival coaches.
            </p>
          </div>
          <span className="text-indigo-400 text-[12px] font-bold whitespace-nowrap ml-2">
            {expanded ? 'Less ▲' : 'How it works →'}
          </span>
        </div>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-2 bg-slate-900/60 rounded-xl p-4 border border-slate-800">
          <p className="text-[13px] text-slate-300 leading-relaxed mb-3">
            Build your game plan by selecting players you believe in and setting performance targets
            — will they go Above or Below the line? Allocate cap points to the players you're counting
            on most and earn points when your calls hit.
          </p>
          <p className="text-[13px] text-slate-300 leading-relaxed mb-3">
            Fill all 7 roster slots with 100 cap points to spend. Minimum 10 cap per player,
            but winning coaches know how you load up matters: stack a{' '}
            <span className="text-amber-400 font-bold">GAMEBREAKER</span> (20+ cap) on your strongest
            call, build around a few{' '}
            <span className="text-indigo-400 font-bold">PLAYMAKERS</span> (10-19 cap), and round out with{' '}
            <span className="text-slate-300 font-bold">ROLE PLAYERS</span> (10 cap). How you structure
            your game plan makes all the difference! 🔥
          </p>
          <p className="text-[13px] text-slate-400 leading-relaxed">
            When the action starts, hit the Scoreboard to see how you're stacking up against rival
            coaches. <span className="text-emerald-400 font-bold">Highest score wins!</span> 👑
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// League Icon Component
// =============================================================================

const LeagueIcon = ({ league, className = "w-4 h-4" }: { league: string; className?: string }) => {
  if (league === 'NFL') {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2}>
        <ellipse cx="12" cy="12" rx="10" ry="6" />
        <path d="M7 12h10M12 8v8" strokeLinecap="round" />
      </svg>
    );
  }
  // Default basketball/generic
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2c-3 4-3 16 0 20M12 2c3 4 3 16 0 20M2 12h20" />
    </svg>
  );
};


// =============================================================================
// MISSION CRITICAL - Hero Card (Incomplete < 60 min)
// =============================================================================

interface MissionCriticalCardProps {
  campaign: WarRoomCampaignCard;
  onCompleteRoster: () => void;
}

const MissionCriticalCard = ({ campaign, onCompleteRoster }: MissionCriticalCardProps) => {
  const gapsRemaining = campaign.slotsTotal - campaign.slotsFilled;
  const impactRemaining = campaign.impactTotal - campaign.impactUsed;
  const minutesToLock = Math.max(0, Math.floor(campaign.timeToLockMs / 60000));
  
  return (
    <section>
      <div className="flex items-center gap-2 mb-3 pl-1">
        <FlameIcon className="w-4 h-4 text-amber-400" />
        <h2 className="text-[11px] font-black text-amber-400 uppercase tracking-[0.15em]">Mission Critical</h2>
      </div>
      
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-950/80 via-slate-900 to-slate-950 border border-amber-800/40 shadow-2xl shadow-amber-900/10">
        <div className="relative z-10 p-5">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="bg-slate-800/80 p-1.5 rounded border border-slate-700/50">
                  <LeagueIcon league={campaign.league} className="w-4 h-4 text-slate-400" />
                </div>
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{campaign.entrantCount} Coaches</span>
              </div>
              <h3 className="text-xl font-black text-white">{campaign.name}</h3>
            </div>
            
            {/* Countdown Badge */}
            <div className="text-right bg-amber-500/10 rounded-xl px-4 py-2 border border-amber-500/20">
              <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wider">Kickoff</p>
              <div className="flex items-baseline gap-1 justify-end">
                <span className="text-3xl font-mono font-black text-amber-400">{minutesToLock}</span>
                <span className="text-sm font-bold text-amber-600">min</span>
              </div>
            </div>
          </div>
          
          {/* Progress Bars Grid */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {/* Roster Progress */}
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Roster</span>
                <span className="text-sm font-mono font-bold text-white">
                  {campaign.slotsFilled}/{campaign.slotsTotal}
                </span>
              </div>
              <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all"
                  style={{ width: `${(campaign.slotsFilled / campaign.slotsTotal) * 100}%` }}
                />
              </div>
              <p className="text-[11px] font-bold text-amber-400 mt-2">
                {gapsRemaining} {gapsRemaining === 1 ? 'position' : 'positions'} open
              </p>
            </div>
            
            {/* Impact Progress */}
            <div className="bg-slate-900/60 rounded-xl p-4 border border-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Impact</span>
                <span className="text-sm font-mono font-bold text-white">
                  {campaign.impactUsed}/{campaign.impactTotal}
                </span>
              </div>
              <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 rounded-full transition-all"
                  style={{ width: `${(campaign.impactUsed / campaign.impactTotal) * 100}%` }}
                />
              </div>
              <p className="text-[11px] font-bold text-indigo-400 mt-2">
                {impactRemaining} available
              </p>
            </div>
          </div>
          
          {/* CTA Button */}
          <button 
            onClick={onCompleteRoster}
            className="w-full bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 text-slate-900 font-black text-sm py-4 rounded-xl flex items-center justify-center gap-3 hover:from-amber-400 hover:to-yellow-300 transition-all active:scale-[0.98] shadow-lg shadow-amber-500/20"
          >
            <TargetIcon className="w-5 h-5" />
            COMPLETE ROSTER
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

// =============================================================================
// ON THE FIELD - Live Campaign Card
// =============================================================================

interface LiveCampaignCardProps {
  campaign: WarRoomCampaignCard;
  onStandings: () => void;
}

const LiveCampaignCard = ({ campaign, onStandings }: LiveCampaignCardProps) => {
  // Calculate execution (assignments hitting) - use wins/totalPredictions if available
  const executingCount = campaign.wins ?? 0;
  const totalAssignments = campaign.totalPredictions ?? campaign.slotsTotal;
  
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 shadow-xl">
      {/* Green Top Accent Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-600 via-emerald-400 to-emerald-600" />
      
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-slate-800 p-2 rounded-lg border border-slate-700/50">
              <LeagueIcon league={campaign.league} className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <h3 className="font-bold text-white">{campaign.name}</h3>
              <p className="text-[11px] text-slate-500">{campaign.entrantCount} coaches</p>
            </div>
          </div>
          
          {/* Live Badge */}
          <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-[11px] font-black text-emerald-400 uppercase">Live</span>
          </div>
        </div>
        
        {/* Scoreboard */}
        <div className="bg-slate-800/40 rounded-xl p-4 mb-4 border border-slate-700/30">
          <div className="flex items-center justify-between">
            {/* Score */}
            <div className="text-center flex-1">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Score</p>
              <p className="text-2xl font-mono font-black text-white">{campaign.currentScore ?? '—'}</p>
            </div>
            
            <div className="w-px h-12 bg-slate-700/50" />
            
            {/* Rank */}
            <div className="text-center flex-1">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Rank</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-2xl font-mono font-black text-indigo-400">
                  {campaign.currentRank ? `#${campaign.currentRank}` : '—'}
                </span>
                {campaign.currentRank && (
                  <span className="text-[11px] text-slate-600">/{campaign.entrantCount}</span>
                )}
              </div>
            </div>
            
            <div className="w-px h-12 bg-slate-700/50" />
            
            {/* Execution */}
            <div className="text-center flex-1">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Execution</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-xl font-mono font-bold text-emerald-400">
                  {executingCount}/{totalAssignments}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* CTA Hierarchy - V9: Single "Scoreboard" CTA for LIVE cards */}
        <div className="space-y-2">
          {/* V9 PRIMARY: SCOREBOARD (was secondary "Standings") */}
          <button 
            onClick={onStandings}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[13px] py-3.5 rounded-xl transition-colors uppercase tracking-wide min-h-[48px]"
          >
            <TrophyIcon className="w-4 h-4" />
            Scoreboard
          </button>
          
          {/* V9 HIDDEN: Field Report - kept for future use, handlers remain in props */}
          {/* 
          <button 
            onClick={onFieldReport}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-[11px] py-3.5 rounded-xl transition-colors uppercase tracking-wide"
          >
            <EyeIcon className="w-4 h-4" />
            Field Report
          </button>
          */}
          
          {/* V9 HIDDEN: Game Plan Review - kept for future use, handlers remain in props */}
          {/* 
          {shouldShowGamePlanReview(campaign) && (
            <button 
              onClick={onGamePlanReview}
              className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-slate-500 font-medium text-[11px] py-2 transition-colors uppercase tracking-wide"
            >
              <FileTextIcon className="w-3 h-3" />
              Game Plan Review
            </button>
          )}
          */}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// IN THE LOCKER ROOM - Ready Campaign Card
// =============================================================================

interface ReadyCampaignCardProps {
  campaign: WarRoomCampaignCard;
  onEditRoster: () => void;
}

const ReadyCampaignCard = ({ campaign, onEditRoster }: ReadyCampaignCardProps) => {
  const navigate = useNavigate();
  const daysUntilLock = Math.max(0, Math.ceil(campaign.timeToLockMs / (1000 * 60 * 60 * 24)));
  
  // Check if entry is already locked (user submitted early)
  const isEntryLocked = campaign.isLocked;
  
  return (
    <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-800/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-500/10 p-2.5 rounded-lg border border-emerald-500/20">
            <CheckCircleIcon className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-bold text-white">{campaign.name}</h3>
            <p className="text-[11px] text-slate-500">
              {campaign.entrantCount} coaches • Locks in {daysUntilLock}d
            </p>
          </div>
        </div>
        
        {/* Badge - changes based on lock status */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${
          isEntryLocked 
            ? 'bg-emerald-500/20 border-emerald-500/30' 
            : 'bg-emerald-500/10 border-emerald-500/20'
        }`}>
          <LockIcon className="w-3 h-3 text-emerald-400" />
          <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wide">
            {isEntryLocked ? 'Roster Locked' : 'Auto-Lock Armed'}
          </span>
        </div>
      </div>
      
      {/* Reassurance Text */}
      <p className="text-[11px] text-slate-600 mt-3 pl-14">
        Prepared — Awaiting Kickoff
      </p>
      
      {/* Action Button - changes based on lock status */}
      <div className="mt-4 pl-14">
        {isEntryLocked ? (
          <button
            onClick={() => navigate(`/pools/${campaign.poolId}/leaderboard`)}
            className="w-full bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold text-[11px] py-2.5 rounded-xl uppercase tracking-wide"
          >
            Scout Other Coaches
          </button>
        ) : (
          <button
            onClick={onEditRoster}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[11px] py-2.5 rounded-xl uppercase tracking-wide"
          >
            Edit Roster
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// FINAL - Completed Campaign Card (V9)
// =============================================================================

interface FinalCampaignCardProps {
  campaign: WarRoomCampaignCard;
  onViewResults: () => void;
}

const FinalCampaignCard = ({ campaign, onViewResults }: FinalCampaignCardProps) => {
  // Calculate performance summary
  const wins = campaign.wins ?? 0;
  const totalPredictions = campaign.totalPredictions ?? campaign.slotsTotal;
  const score = campaign.currentScore ?? 0;
  
  return (
    <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-800/50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="bg-slate-800/50 p-2.5 rounded-lg border border-slate-700/30">
            <CheckCircleIcon className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h3 className="font-bold text-slate-300">{campaign.name}</h3>
            <p className="text-[11px] text-slate-600">
              {campaign.entrantCount} coaches • Final
            </p>
          </div>
        </div>
        
        {/* Score Badge */}
        <div className={`px-3 py-1.5 rounded-full ${
          score >= 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <span className={`text-[13px] font-mono font-bold ${
            score >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {score >= 0 ? '+' : ''}{score}
          </span>
        </div>
      </div>
      
      {/* Performance Summary */}
      <div className="mb-3 pl-14">
        <p className="text-[12px] text-slate-500">
          {wins} of {totalPredictions} calls hit
          {campaign.currentRank && ` • Finished #${campaign.currentRank}`}
        </p>
      </div>
      
      {/* View Results Button */}
      <div className="pl-14">
        <button
          onClick={onViewResults}
          className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-[11px] py-2.5 rounded-xl uppercase tracking-wide"
        >
          View Results
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// Empty State Component
// =============================================================================

const EmptyState = () => (
  <div className="text-center py-16 px-4">
    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
      <TrophyIcon className="w-8 h-8 text-slate-600" />
    </div>
    <h2 className="text-xl font-bold text-slate-400 mb-2">No Active Campaigns</h2>
    <p className="text-slate-600 text-sm max-w-xs mx-auto">
      You're not currently coaching in any pools. Join a pool to start building your roster.
    </p>
  </div>
);

// =============================================================================
// Main War Room Page - V6 Full Visual Implementation
// =============================================================================

export function WarRoomPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.user_id || null;
  
  
  
  // Fetch all pools
  const { pools, loading: poolsLoading, error: poolsError, refetch: refetchPools } = usePools();
  
  // Fetch user's memberships
  const { memberships, loading: membershipsLoading, refetch: refetchMemberships } = useMemberships(userId || '');
  
  // Get pool IDs user is a member of
  const userPoolIds = useMemo(() => {
    return memberships.map(m => m.pool_id);
  }, [memberships]);
  
  // Build membership lookup map for metrics
  const membershipByPoolId = useMemo(() => {
    return new Map(memberships.map(m => [m.pool_id, m as PoolMembership]));
  }, [memberships]);
  
  // Calculate aggregated Strategy Effectiveness (Option A - Weighted)
  const aggregatedEffectiveness = useMemo(() => {
    const sumWon = memberships.reduce((acc, m) => acc + ((m as any).total_won ?? 0), 0);
    const sumLost = memberships.reduce((acc, m) => acc + ((m as any).total_lost ?? 0), 0);
    const totalRisked = sumWon + sumLost;
    return totalRisked > 0 ? Math.round((sumWon / totalRisked) * 100) : 0;
  }, [memberships]);
  
  const hasRiskedUnits = memberships.some(m => ((m as any).total_won ?? 0) + ((m as any).total_lost ?? 0) > 0);
  
  // Filter pools to user's memberships and GM mode
  const userGMPools = useMemo(() => {
    return pools.filter(p => 
      userPoolIds.includes(p.pool_id) && 
      p.mode === 'gm'
    );
  }, [pools, userPoolIds]);
  
  // Fetch entries for user's GM pools
  const { 
    entries, 
    loading: entriesLoading, 
    refresh: refetchEntries 
  } = useWarRoomOverview(userId, userGMPools.map(p => p.pool_id));
  
  // Map to display cards with membership data
  const campaignCards = useMemo(() => {
    return mapToWarRoomCards(userGMPools, entries, membershipByPoolId);
  }, [userGMPools, entries, membershipByPoolId]);
  
  // Triage logic (hero logic is correct in mappers - unchanged)
  const heroCandidate = useMemo(() => getHeroCandidate(campaignCards), [campaignCards]);
  const criticalList = useMemo(
    () => getCriticalListCampaigns(campaignCards, heroCandidate?.poolId || null),
    [campaignCards, heroCandidate]
  );
  const liveCampaigns = useMemo(() => getLiveCampaigns(campaignCards), [campaignCards]);
  const readyCampaigns = useMemo(
    () => sortByLockTime(getCompleteCampaigns(campaignCards)),
    [campaignCards]
  );
  // V9: Add FINAL campaigns section
  const finalCampaigns = useMemo(() => getFinalCampaigns(campaignCards), [campaignCards]);
  // FIX: Slots full but cap not fully deployed — "UPDATE ROSTER" treatment
  const capAvailableCampaigns = useMemo(
    () => sortByLockTime(getSlotsFullCapAvailable(campaignCards)),
    [campaignCards]
  );
  
  // Counts for status pills
  const liveCount = liveCampaigns.length;
  const incompleteCount = (heroCandidate ? 1 : 0) + criticalList.length + capAvailableCampaigns.length;
  const readyCount = readyCampaigns.length;
  
  
  // Refresh handler
  const handleRefresh = () => {
    refetchPools();
    refetchMemberships();
    refetchEntries();
  };
  
  // ==========================================================================
  // Navigation handlers
  // ==========================================================================
  
  const navigateToTunnel = (poolId: string) => {
    navigate(`/pools/${poolId}/tunnel`);
  };
  

  
  const navigateToStandings = (poolId: string) => {
    navigate(`/pools/${poolId}/leaderboard`);
  };
  
  
  // Loading state
  const isLoading = poolsLoading || membershipsLoading || entriesLoading;
  const hasNoCampaigns = !isLoading && campaignCards.length === 0;

  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      
      {/* ================================================================== */}
      {/* COMMAND HEADER - Dark Theme */}
      {/* ================================================================== */}
      <header className="bg-slate-950 border-b border-slate-800/50">
        <div className="px-5 pt-6 pb-4">
          <div className="flex items-center justify-between">
            {/* Coach Identity */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-indigo-800 flex items-center justify-center text-2xl font-black shadow-lg shadow-indigo-500/10 border border-indigo-500/30">
                {user?.display_name?.[0]?.toUpperCase() || user?.user_id?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-600 uppercase tracking-[0.2em]">Head Coach</p>
                <h1 className="text-xl font-black text-white leading-tight">
                  {user?.display_name || user?.user_id || 'Guest'}
                </h1>
              </div>
            </div>
            
            {/* Strategy Effectiveness */}
            <div className="text-right">
              <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Strategy Effectiveness</p>
              <span className="text-2xl font-mono font-black text-emerald-400">
                {hasRiskedUnits ? `${aggregatedEffectiveness}%` : '—'}
              </span>
            </div>
          </div>
        </div>
        
        {/* Status Pills + Refresh */}
        <div className="px-5 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {liveCount > 0 && (
              <div className="flex items-center gap-2 bg-emerald-500/10 rounded-full px-3 py-1.5 border border-emerald-500/20">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-[11px] font-bold text-emerald-400">{liveCount} Live</span>
              </div>
            )}
            {incompleteCount > 0 && (
              <div className="flex items-center gap-2 bg-amber-500/10 rounded-full px-3 py-1.5 border border-amber-500/20">
                <AlertTriangleIcon className="w-3 h-3 text-amber-400" />
                <span className="text-[11px] font-bold text-amber-400">{incompleteCount} Incomplete</span>
              </div>
            )}
            {readyCount > 0 && (
              <div className="flex items-center gap-2 bg-slate-800/50 rounded-full px-3 py-1.5 border border-slate-700/50">
                <CheckCircleIcon className="w-3 h-3 text-slate-500" />
                <span className="text-[11px] font-bold text-slate-500">{readyCount} Ready</span>
              </div>
            )}
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshIcon className={`w-5 h-5 text-slate-500 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {/* ================================================================== */}
      {/* MAIN CONTENT */}
      {/* ================================================================== */}
      <main className="px-4 py-6 space-y-6">
        
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-slate-500">Loading campaigns...</div>
          </div>
        )}
        
        {/* Error State */}
        {poolsError && !isLoading && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 text-red-400 text-sm">
            {poolsError}
          </div>
        )}
        
        {/* Empty State */}
        {hasNoCampaigns && <EmptyState />}
        
        {/* Game Objective Guidance - V9 Mockup */}
        {!isLoading && !hasNoCampaigns && <ObjectiveBanner />}
        
        {/* Campaign Sections */}
        {!isLoading && !hasNoCampaigns && (
          <>
            {/* ============================================================ */}
            {/* MISSION CRITICAL - Hero Card (Incomplete < 60 min) */}
            {/* ============================================================ */}
            {heroCandidate && (
              <MissionCriticalCard
                campaign={heroCandidate}
                onCompleteRoster={() => navigateToTunnel(heroCandidate.poolId)}
              />
            )}
            
            {/* ============================================================ */}
            {/* NEEDS ATTENTION - Other Incomplete (> 60 min, no hero styling) */}
            {/* ============================================================ */}
            {criticalList.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 pl-1">
                  <AlertTriangleIcon className="w-4 h-4 text-amber-500" />
                  <h2 className="text-[11px] font-black text-amber-500 uppercase tracking-[0.15em]">Needs Attention</h2>
                </div>
                
                <div className="space-y-3">
                  {criticalList.map(campaign => {
                    const gapsRemaining = campaign.slotsTotal - campaign.slotsFilled;
                    return (
                      <div 
                        key={campaign.poolId}
                        className="bg-slate-900/60 rounded-xl p-4 border border-amber-900/30"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="bg-slate-800 p-2 rounded-lg border border-slate-700/50">
                              <LeagueIcon league={campaign.league} className="w-4 h-4 text-slate-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">{campaign.name}</h3>
                              <p className="text-[11px] text-slate-500">{campaign.entrantCount} coaches</p>
                            </div>
                          </div>
                          <span className="text-[11px] font-bold text-amber-400">
                            {gapsRemaining} {gapsRemaining === 1 ? 'gap' : 'gaps'}
                          </span>
                        </div>
                        
                        <button 
                          onClick={() => navigateToTunnel(campaign.poolId)}
                          className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold text-[11px] py-2.5 rounded-lg transition-colors uppercase tracking-wide flex items-center justify-center gap-2"
                        >
                          Fill Gaps <ChevronRightIcon className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            
            

            {/* ============================================================ */}
            {/* OPTIMIZE - Slots full, units not fully deployed */}
            {/* ============================================================ */}
            {capAvailableCampaigns.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 pl-1">
                  <TargetIcon className="w-4 h-4 text-indigo-400" />
                  <h2 className="text-[11px] font-black text-indigo-400 uppercase tracking-[0.15em]">Optimize</h2>
                </div>
                
                <div className="space-y-3">
                  {capAvailableCampaigns.map(campaign => {
                    const unitsAvailable = campaign.impactTotal - campaign.impactUsed;
                    return (
                      <div 
                        key={campaign.poolId}
                        className="bg-slate-900/60 rounded-xl p-4 border border-indigo-900/30"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="bg-slate-800 p-2 rounded-lg border border-slate-700/50">
                              <LeagueIcon league={campaign.league} className="w-4 h-4 text-slate-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">{campaign.name}</h3>
                              <p className="text-[11px] text-slate-500">{campaign.entrantCount} coaches</p>
                            </div>
                          </div>
                          <span className="text-[11px] font-bold text-indigo-400">
                            {unitsAvailable} {unitsAvailable === 1 ? 'unit' : 'units'} available
                          </span>
                        </div>
                        
                        <button 
                          onClick={() => navigateToTunnel(campaign.poolId)}
                          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-[11px] py-2.5 rounded-lg transition-colors uppercase tracking-wide flex items-center justify-center gap-2"
                        >
                          Update Roster <ChevronRightIcon className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            
            {/* ============================================================ */}
            {/* ON THE FIELD - Live Campaigns */}
            {/* ============================================================ */}
            {liveCampaigns.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 pl-1">
                  <PlayIcon className="w-4 h-4 text-emerald-400" />
                  <h2 className="text-[11px] font-black text-emerald-400 uppercase tracking-[0.15em]">On The Field</h2>
                </div>
                
                <div className="space-y-4">
                  {liveCampaigns.map(campaign => (
                    <LiveCampaignCard
                      key={campaign.poolId}
                      campaign={campaign}
                      onStandings={() => navigateToStandings(campaign.poolId)}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {/* ============================================================ */}
            {/* IN THE LOCKER ROOM - Ready Campaigns */}
            {/* ============================================================ */}
            {readyCampaigns.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 pl-1">
                  <ShieldIcon className="w-4 h-4 text-slate-500" />
                  <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.15em]">In The Locker Room</h2>
                </div>
                
                <div className="space-y-3">
                  {readyCampaigns.map(campaign => (
                    <ReadyCampaignCard
                      key={campaign.poolId}
                      campaign={campaign}
                      onEditRoster={() => navigateToTunnel(campaign.poolId)}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {/* ============================================================ */}
            {/* FINAL - Completed Campaigns (V9) */}
            {/* ============================================================ */}
            {finalCampaigns.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3 pl-1">
                  <CheckCircleIcon className="w-4 h-4 text-slate-500" />
                  <h2 className="text-[11px] font-black text-slate-500 uppercase tracking-[0.15em]">Final — Review</h2>
                </div>
                
                <div className="space-y-3">
                  {finalCampaigns.map(campaign => (
                    <FinalCampaignCard
                      key={campaign.poolId}
                      campaign={campaign}
                      onViewResults={() => navigateToStandings(campaign.poolId)}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      
      {/* Bottom Spacer */}
      <div className="h-8" />
    </div>
  );
}

export default WarRoomPage;
